import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { adminApi, type AdminReferralRow, type ReferralDecision, type ReferralStatus } from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  allowedDecisions, buildDecision, DECISION_LABEL, NOTE_MAX, REFERRAL_STATUS_OPTIONS,
} from "@/lib/referrals";
import { referralTone } from "@/lib/status-tone";
import { formatLondon, isoUtc } from "@/lib/format-time";
import { queryTableProps } from "@/lib/data-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/DataTable";
import { DetailDrawer, DetailRow } from "@/components/DetailDrawer";
import { StateFilterSelect } from "@/components/StateFilterSelect";
import { StatusPill } from "@/components/StatusPill";

export const Route = createFileRoute("/_authed/referrals")({
  component: ReferralsPage,
});

const PAGE_SIZE = 50;

function When({ ms }: { ms: number | null }) {
  return ms ? <span title={isoUtc(ms)}>{formatLondon(ms)}</span> : <>—</>;
}

function UidLink({ uid }: { uid: string }) {
  return (
    <Link to="/users/$uid" params={{ uid }} className="font-mono text-xs text-primary hover:underline">
      {uid.slice(0, 10)}…
    </Link>
  );
}

function ReferralsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<"" | ReferralStatus>("review");
  const [referrerUid, setReferrerUid] = useState("");
  const [refereeUid, setRefereeUid] = useState("");
  const debReferrer = useDebouncedValue(referrerUid);
  const debReferee = useDebouncedValue(refereeUid);
  const [selected, setSelected] = useState<AdminReferralRow | null>(null);

  const filters = useMemo(
    () => ({
      status: status || undefined,
      referrerUid: debReferrer.trim() || undefined,
      refereeUid: debReferee.trim() || undefined,
      limit: PAGE_SIZE,
    }),
    [status, debReferrer, debReferee],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.referrals.list(filters),
    queryFn: async ({ pageParam }) =>
      (await adminApi.listReferrals()({ ...filters, cursor: pageParam ?? null })).data,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });
  const rows = query.data?.pages.flatMap((p) => p.referrals) ?? [];

  const columns = useMemo<ColumnDef<AdminReferralRow, unknown>[]>(
    () => [
      { header: "Referee", cell: ({ row }) => <UidLink uid={row.original.refereeUid} /> },
      { header: "Referrer", cell: ({ row }) => <UidLink uid={row.original.referrerUid} /> },
      {
        header: "Status",
        cell: ({ row }) => <StatusPill tone={referralTone(row.original.status)}>{row.original.status}</StatusPill>,
      },
      {
        header: "Reason / signals",
        cell: ({ row }) => {
          const r = row.original;
          const parts = [r.reviewReason, r.rejectReason, ...r.signals].filter(Boolean);
          return <span className="text-xs">{[...new Set(parts)].join(", ") || "—"}</span>;
        },
      },
      { header: "Registered", cell: ({ row }) => <When ms={row.original.createdAt} /> },
      { header: "Eligible", cell: ({ row }) => <When ms={row.original.eligibleAt} /> },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Referrals</h1>
        <p className="text-sm text-muted-foreground">
          Review queue for friend referrals. Hashed device, IP and phone identifiers never reach
          this page. Every decision is audit-logged.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <Field label="Status">
            <StateFilterSelect value={status} onChange={setStatus} options={REFERRAL_STATUS_OPTIONS} allLabel="All statuses" />
          </Field>
          <Field label="Referrer UID">
            <Input value={referrerUid} onChange={(e) => setReferrerUid(e.target.value)} className="w-56" placeholder="uid" />
          </Field>
          <Field label="Referee UID">
            <Input value={refereeUid} onChange={(e) => setRefereeUid(e.target.value)} className="w-56" placeholder="uid" />
          </Field>
        </CardContent>
      </Card>

      <DataTable
        data={rows}
        columns={columns}
        rowKey={(row) => row.refereeUid}
        emptyMessage="No referrals match this filter."
        onRowClick={(row) => setSelected(row)}
        {...queryTableProps(query)}
        hasMore={!!query.hasNextPage}
        isFetchingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
      />

      {selected ? (
        <DecisionDrawer
          row={selected}
          onClose={() => setSelected(null)}
          onDecided={(row) => {
            setSelected(row);
            void qc.invalidateQueries({ queryKey: queryKeys.referrals.all });
          }}
        />
      ) : null}
    </div>
  );
}

function DecisionDrawer({
  row,
  onClose,
  onDecided,
}: {
  row: AdminReferralRow;
  onClose: () => void;
  onDecided: (row: AdminReferralRow) => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const decisions = allowedDecisions(row.status);

  const decide = useMutation({
    mutationFn: async (decision: ReferralDecision) => {
      const built = buildDecision(row.refereeUid, decision, note);
      if (!built.ok) throw new Error(built.error);
      return (await adminApi.decideReferral()(built.input)).data;
    },
    onSuccess: (updated) => {
      setNote("");
      setError(null);
      onDecided(updated);
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(decision: ReferralDecision) {
    const built = buildDecision(row.refereeUid, decision, note);
    if (!built.ok) {
      setError(built.error);
      return;
    }
    const extra = decision === "clawback" ? " The referrer is notified and any tag from this referral is revoked." : "";
    if (!window.confirm(`${DECISION_LABEL[decision]} this referral?${extra}`)) return;
    decide.mutate(decision);
  }

  return (
    <DetailDrawer
      title="Referral"
      subtitle={row.refereeUid}
      onClose={onClose}
      footer={
        decisions.length ? (
          <div className="flex gap-2">
            {decisions.map((d) => (
              <Button
                key={d}
                className="flex-1"
                variant={d === "approve" ? "default" : "outline"}
                disabled={decide.isPending}
                onClick={() => submit(d)}
              >
                {DECISION_LABEL[d]}
              </Button>
            ))}
          </div>
        ) : undefined
      }
    >
      <div className="space-y-2">
        <DetailRow label="Status">
          <StatusPill tone={referralTone(row.status)}>{row.status}</StatusPill>
        </DetailRow>
        <DetailRow label="Referee"><UidLink uid={row.refereeUid} /></DetailRow>
        <DetailRow label="Referrer"><UidLink uid={row.referrerUid} /></DetailRow>
        <DetailRow label="Signals">{row.signals.length ? row.signals.join(", ") : "—"}</DetailRow>
        <DetailRow label="Review reason">{row.reviewReason ?? "—"}</DetailRow>
        <DetailRow label="Reject reason">{row.rejectReason ?? "—"}</DetailRow>
        <DetailRow label="Registered"><When ms={row.createdAt} /></DetailRow>
        <DetailRow label="Verified"><When ms={row.verifiedAt} /></DetailRow>
        <DetailRow label="Eligible from"><When ms={row.eligibleAt} /></DetailRow>
        <DetailRow label="Awarded"><When ms={row.awardedAt} /></DetailRow>
        <DetailRow label="Resolved"><When ms={row.resolvedAt} /></DetailRow>
        <DetailRow label="Tag">{row.tagId ? <span className="font-mono text-xs">{row.tagId}</span> : "—"}</DetailRow>
        {row.reviewedBy ? (
          <DetailRow label="Last review">
            <span className="font-mono text-xs">{row.reviewedBy}</span> · <When ms={row.reviewedAt} />
            {row.reviewNote ? <div className="text-xs text-muted-foreground">{row.reviewNote}</div> : null}
          </DetailRow>
        ) : null}
      </div>

      {decisions.length ? (
        <div className="mt-6 space-y-2">
          <Field label={decisions.includes("approve") ? "Note (required to reject or claw back)" : "Note (required)"}>
            <Input value={note} maxLength={NOTE_MAX} onChange={(e) => setNote(e.target.value)} placeholder="Why?" />
          </Field>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
      ) : (
        <p className="mt-6 text-sm text-muted-foreground">This referral is resolved; no further decision applies.</p>
      )}
    </DetailDrawer>
  );
}
